import React from "react";

export function trimPath(path) {
  return path.split("/")[1];
}

export function removeDuplicatesBy(keyFn, array) {
  var mySet = new Set();
  return array.filter(function (x) {
    var key = keyFn(x),
      isNew = !mySet.has(key);
    if (isNew) mySet.add(key);
    return isNew;
  });
}

export function replaceUnderscores(str) {
  const frags = str.split("_");
  return frags.join(" ");
}

// Define a default UI for filtering
export function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length;

  return (
    <input
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
      }}
      placeholder={`Search ${count} records...`}
    />
  );
}

// This is a custom filter UI for selecting
// a unique option from a list
export function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id },
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row) => {
      options.add(row.values[id]);
    });
    const optionsArr = [...options.values()];
    // strip out any null values
    return optionsArr
      .filter((option) => option)
      .sort((a, b) => a.localeCompare(b));
  }, [id, preFilteredRows]);

  // Render a multi-select box
  return (
    <select
      style={{ height: "20px" }}
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

// This is a custom UI for our 'between' or number range
// filter. It uses two number boxes and filters rows to
// ones that have values between the two
export function NumberRangeColumnFilter({
  column: { filterValue = [], preFilteredRows, setFilter, id },
}) {
  const [min] = React.useMemo(() => {
    let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    preFilteredRows.forEach((row) => {
      min = Math.min(row.values[id], min);
      max = Math.max(row.values[id], max);
    });
    return [min, max];
  }, [id, preFilteredRows]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <input
        value={filterValue[0] || ""}
        type="number"
        onChange={(e) => {
          const val = e.target.value;
          setFilter((old = []) => [
            val ? parseInt(val, 10) : undefined,
            old[1],
          ]);
        }}
        placeholder={`Min (${min})`}
        style={{
          width: "70px",
          height: "20px",
          marginRight: "0.5rem",
        }}
      />
    </div>
  );
}

// This is a custom filter UI for selecting
// a unique option from 2 arrays
// Pass in the properties as args
export function CustomSelectColumnFilter(
  { column: { filterValue, setFilter, preFilteredRows } },
  property1,
  property2,
) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const player1Arr = preFilteredRows
      .map((row) => row.values[property1])
      .filter((value, i, self) => self.indexOf(value) === i)
      .filter((value) => value !== null);

    const player2Arr = preFilteredRows
      .map((row) => row.values[property2])
      .filter((value, i, self) => self.indexOf(value) === i)
      .filter((value) => value !== null);

    const players = player1Arr
      .concat(player2Arr)
      .sort((a, b) => a.localeCompare(b));
    const uniquePlayers = removeDuplicatesBy((x) => x, players);

    return uniquePlayers;
  }, [preFilteredRows, property1, property2]);

  // Render a multi-select box
  return (
    <select
      style={{ height: "20px", minWidth: "40px" }}
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

export function getLeagueInfo(leagueId) {
  switch (leagueId) {
    case "DF2F34CF-1736-4FD8-B9B1-04D7BA5450DF":
      return {
        name: "NBA",
        schedule: "https://au.global.nba.com/schedule/",
        validMarketThreshold: 1,
      };
    case "5DB88974-FA00-4A02-8EC2-5753A3BD0D2C":
      return {
        name: "AFL",
        schedule: "https://www.afl.com.au/fixture",
        validMarketThreshold: 1,
      };
    case "BC4EAC22-C959-4FF1-8087-5DFA9A024992":
      return {
        name: "NFL",
        schedule: "https://www.nfl.com/schedules/",
        validMarketThreshold: 1,
      };
    case "6266D24C-1CA3-4E23-A344-A15EE3168280":
      return {
        name: "NBL",
        schedule: "https://www.nhl.com/schedule",
        validMarketThreshold: 1,
      };
    default:
      return {};
  }
}

export function formatDate(inputDate) {
  const parts = inputDate.split("-");
  if (parts.length !== 3) {
    throw new Error("Invalid date format. Expected 'YYYY-MM-DD'.");
  }

  const [year, month, day] = parts;
  return `${day}-${month}-${year}`;
}

export function reverseFormatDate(inputDate) {
  const parts = inputDate.split("-");
  if (parts.length !== 3) {
    throw new Error("Invalid date format. Expected 'DD-MM-YYYY'.");
  }

  const [day, month, year] = parts;
  return `${year}-${month}-${day}`;
}
